/**
 * @fileoverview Hook de Data Layer para gestión de datos de plantillas API y configuraciones de archivos adjuntos en WhatsApp.
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades, enfocado en extracción de variables dinámicas y configuración de archivos según tipo de plantilla.
 * Incluye optimizaciones de performance, manejo robusto de errores y documentación completa de flujos y tipos de datos.
 * @module useApiTemplateData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 3.1.0 - Optimización de performance, documentación JSDoc completa, separación de capas y manejo avanzado de errores
 */
import { useMemo, useCallback } from 'react';
import fileUtils from '../../../../util/fileUtils';
import {
  MAX_FILE_SIZE_TRANSFER,
  MAX_VIDEO_SIZE_TRANSFER,
  MAX_IMAGE_SIZE_TRANSFER_WS
} from '../../../../constants/chatFileSizes';

/**
 * Hook de Data Layer para gestión de datos de plantillas API y configuración de archivos adjuntos.
 */
const useApiTemplateData = (selectedTemplate, t) => {
  /**
   * Determina la configuración de archivos adjuntos según el tipo de plantilla seleccionada.
   * Para carousel, determina la configuración según el headerType del primer card.
   */
  const fileConfig = useMemo(() => {
    let templateType = selectedTemplate?.templateType;

    if (templateType === 'carousel') {
      templateType = selectedTemplate?.cards?.[0]?.headerType?.toLowerCase();
    }

    const configs = {
      image: {
        accept: 'image/jpeg, image/jpg, image/png',
        maxSize: MAX_IMAGE_SIZE_TRANSFER_WS,
        validTypes: ['image/jpg', 'image/jpeg', 'image/png'],
        validator: fileUtils.isValidImageSizeForWs
      },
      document: {
        accept: 'application/pdf',
        maxSize: MAX_FILE_SIZE_TRANSFER,
        validTypes: ['application/pdf'],
        validator: fileUtils.isMaxFileSize
      },
      video: {
        accept: 'video/mp4, video/3gpp',
        maxSize: MAX_VIDEO_SIZE_TRANSFER,
        validTypes: ['video/mp4', 'video/3gpp'],
        validator: fileUtils.isValidVideoSizeForWs
      }
    };

    // Manejo para templates no-carousel (lógica original)
    return configs[templateType] || {
      accept: '',
      maxSize: MAX_FILE_SIZE_TRANSFER,
      validTypes: [],
      validator: null
    };
  }, [selectedTemplate?.templateType, selectedTemplate?.cards]);

  /**
   * Extrae variables dinámicas del texto de la plantilla API.
   */
  const extractDynamicVariables = useCallback((text) => {
    if (!text) return { fieldNames: [], fields: {} };

    const fieldNames = [];
    const fields = {};
    const textArray = text.split(/({{[1-9]+[0-9]*}})/g);

    textArray.forEach((key) => {
      if (/{{[1-9]+[0-9]*}}/.test(key)) {
        const id = key.replaceAll('{{', '').replaceAll('}}', '');
        const fieldName = `variable-${id}`;
        fieldNames.push(fieldName);
        fields[fieldName] = {
          id,
          fieldName,
          validation: t ? require('yup').string().required(t('validation.fillAllVariables')) : null
        };
      }
    });

    return { fieldNames, fields };
  }, [t]);

  /**
   * Estructura de datos con variables dinámicas extraídas de la plantilla seleccionada.
   */
  const dynamicFieldsData = useMemo(() => {
    if (!selectedTemplate) {
      return { main: null, cards: [] };
    }

    const cards = [];
    let main = null;
    let variableCounter = 1;

    // Función auxiliar para procesar variables dinámicas de cualquier texto
    const extractVariablesFromText = (text, groupInfo) => {
      if (!text) return null;
      const groupVariables = [];
      const groupFields = {};
      const textArray = text.split(/({{[1-9]+[0-9]*}})/g);
      textArray.forEach((key) => {
        if (/{{[1-9]+[0-9]*}}/.test(key)) {
          const originalId = key.replaceAll('{{', '').replaceAll('}}', '');
          const fieldName = `variable-${variableCounter}`;
          const fieldData = {
            id: variableCounter,
            originalId,
            fieldName,
            position: variableCounter,
            validation: t ? require('yup').string().required(t('validation.fillAllVariables')) : null
          };
          groupVariables.push({ fieldName, originalId, variableNumber: variableCounter });
          groupFields[fieldName] = fieldData;
          variableCounter++;
        }
      });
      if (groupVariables.length > 0) {
        return {
          ...groupInfo,
          variables: groupVariables,
          fields: groupFields,
          count: groupVariables.length
        };
      }
      return null;
    };

    // Procesar texto principal
    const mainGroup = extractVariablesFromText(selectedTemplate.text, {
      type: 'main',
      section: 'text',
      title: 'Texto Principal',
      index: 0
    });
    if (mainGroup) main = mainGroup;

    // Procesar cards si existen
    if (selectedTemplate.cards && selectedTemplate.cards.length > 0) {
      selectedTemplate.cards.forEach((card, cardIndex) => {
        // Procesar body de la carta
        const cardGroupData = extractVariablesFromText(card.body, {
          type: 'card',
          section: 'body',
          title: `Card ${cardIndex + 1} - Contenido`,
          cardIndex,
          index: cardIndex,
          headerType: card.headerType
        });

        // Procesar botones y guardar los resultados en buttonVariables
        const buttonVariables = [];
        if (card.buttons) {
          card.buttons.forEach((button, buttonIndex) => {
            const buttonGroupData = extractVariablesFromText(button.url, {
              type: 'button',
              section: 'url',
              title: `Card ${cardIndex + 1} - Botón ${buttonIndex + 1}`,
              cardIndex,
              buttonIndex,
              index: `${cardIndex}-${buttonIndex}`,
              headerType: card.headerType
            });
            if (buttonGroupData) buttonVariables.push(buttonGroupData);
          });
        }

        // Determinar si la card debe incluirse (siempre en carousels para poder subir archivos)
        const hasMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(card.headerType);

        if (cardGroupData || buttonVariables.length > 0 || hasMedia) {
          cards.push({
            type: 'card',
            cardIndex,
            headerType: card.headerType,
            body: cardGroupData || {
              title: `Card ${cardIndex + 1}`,
              variables: [],
              headerType: card.headerType
            },
            buttons: buttonVariables
          });
        }
      });
    }

    return { main, cards };
  }, [selectedTemplate, t]);

  /**
   * Retorna los datos de configuración y variables dinámicas para plantillas API.
   *
   * @returns {Object} { fileConfig, dynamicFieldsData, extractDynamicVariables }
   */
  return useMemo(() => ({
    fileConfig,
    dynamicFieldsData,
    extractDynamicVariables
  }), [fileConfig, dynamicFieldsData, extractDynamicVariables]);
};

export default useApiTemplateData;
